import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { readWorkspace, branchDisplay } from "../utils/workspace";
import { 
  Upload, FileSpreadsheet, X, CheckCircle, AlertCircle, Loader2, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const ImportCases = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [courtType, setCourtType] = useState("Μονομελές");
  const [courtBranch, setCourtBranch] = useState(() => readWorkspace(user)?.branch || "Πρωτοδικείο Αθηνών");
  const [branches, setBranches] = useState([]);
  const [defendantResidence, setDefendantResidence] = useState("domestic");
  const [isStateParty, setIsStateParty] = useState(false);
  const [duplicateAction, setDuplicateAction] = useState("update");
  const gdprCompliance = true; // iter109: υποχρεωτική
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    axios.get(`${API}/branches`).then(r => setBranches((r.data?.branches || []).map(b => b.label))).catch(() => {});
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleFile = (selectedFile) => {
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    if (!['.xlsx', '.xls', '.ods', '.csv'].includes(ext)) {
      toast.error("Μη έγκυρος τύπος αρχείου. Υποστηρίζονται: Excel, LibreOffice, CSV");
      return;
    }
    setFile(selectedFile);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const params = new URLSearchParams({
        court_type: courtType,
        court_branch: courtBranch,
        defendant_residence: defendantResidence,
        is_state_party: isStateParty,
        duplicate_action: duplicateAction,
        gdpr_compliance: gdprCompliance,
      });
      const response = await axios.post(`${API}/cases/import?${params}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      const msg = [];
      if (response.data.imported_count > 0) msg.push(`${response.data.imported_count} νέες`);
      if (response.data.updated_count > 0) msg.push(`${response.data.updated_count} ενημερώθηκαν`);
      if (msg.length > 0) toast.success(msg.join(", "));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Σφάλμα κατά την εισαγωγή");
      setResult({ error: error.response?.data?.detail || "Σφάλμα" });
    } finally {
      setUploading(false);
    }
  };

  const expectedColumns = [
    { name: "Γ.Α.Κ. Αριθμός", required: true },
    { name: "Γ.Α.Κ. Έτος", required: true },
    { name: "Ε.Α.Κ.", required: true },
    { name: "Ημερομηνία Κατάθεσης", required: true },
    { name: "Αντικείμενο", required: false },
    { name: "Υποαντικείμενο", required: false },
    { name: "Είδος Δικογραφίας", required: false },
    { name: "Στοιχεία Αιτούντων/Εναγόντων", required: false },
    { name: "Στοιχεία Εναγόμενων", required: false },
    { name: "Τελευταία Δικάσιμος", required: false },
    { name: "Στοιχεία Δημοσίευσης", required: false },
    { name: "Διαδικασία", required: false },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Εισαγωγή Υποθέσεων</h1>
        <p className="text-slate-500 mt-1">Εισάγετε υποθέσεις από αρχείο Excel ή LibreOffice</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" />Αρχείο Εισαγωγής</CardTitle>
          <CardDescription>Μοναδική ταυτότητα: ΓΑΚ Αριθμός + Έτος. Υποστηριζόμενοι τύποι: .xlsx, .xls, .ods, .csv</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Court Type */}
          <div>
            <Label>Τύπος Δικαστηρίου</Label>
            <Select value={courtType} onValueChange={setCourtType}>
              <SelectTrigger className="mt-1" data-testid="court-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Μονομελές">Μονομελές Πρωτοδικείο</SelectItem>
                <SelectItem value="Πολυμελές">Πολυμελές Πρωτοδικείο</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Court Branch / Κατάστημα */}
          <div>
            <Label>Κατάστημα</Label>
            <Select value={courtBranch} onValueChange={setCourtBranch}>
              <SelectTrigger className="mt-1" data-testid="court-branch-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {branches.map(b => (
                  <SelectItem key={b} value={b}>{branchDisplay(b)}</SelectItem>
                ))}
                {!branches.includes(courtBranch) && (
                  <SelectItem value={courtBranch}>{branchDisplay(courtBranch)}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Deadline Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Κατοικία Εναγομένων</Label>
              <Select value={defendantResidence} onValueChange={setDefendantResidence}>
                <SelectTrigger className="mt-1" data-testid="residence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domestic">Εσωτερικό (30 ημ. επίδοση)</SelectItem>
                  <SelectItem value="abroad">Εξωτερικό (30 ημ. επίδοση)</SelectItem>
                  <SelectItem value="unknown">Αγνώστου Διαμονής (30 ημ. επίδοση)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Διάδικος το Δημόσιο;</Label>
              <Select value={isStateParty ? "yes" : "no"} onValueChange={(v) => setIsStateParty(v === "yes")}>
                <SelectTrigger className="mt-1" data-testid="state-party-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Όχι</SelectItem>
                  <SelectItem value="yes">Ναι (εξαίρεση 1/7-15/9)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* GDPR — υποχρεωτική ανωνυμοποίηση (iter109) */}
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="gdpr-compliance-notice">
            <div className="flex items-center gap-3">
              <input type="checkbox" id="gdpr-compliance" checked disabled className="w-4 h-4 text-blue-600 rounded border-blue-300" data-testid="gdpr-compliance-checkbox" />
              <Label htmlFor="gdpr-compliance" className="text-blue-800 font-medium">
                Συμμόρφωση με GDPR — Υποχρεωτική ανωνυμοποίηση διαδίκων (πλήρης κατακερματισμός)
              </Label>
            </div>
            <p className="text-xs text-blue-600 mt-1 ml-7">
              Τα ονοματεπώνυμα των διαδίκων ΔΕΝ αποθηκεύονται: αντικαθίστανται οριστικά από ψευδώνυμο «ΔΙΑΔ-XXXXXXXXXXXX» (HMAC-SHA256) για όλους τους χρήστες και τον διαχειριστή. Η αναζήτηση λειτουργεί με το ακριβές ονοματεπώνυμο. Εξαίρεση: «Ελληνικό Δημόσιο» (για σωστό υπολογισμό προθεσμιών).
            </p>
          </div>

          {/* Duplicate Action */}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
            <Label className="text-amber-800 font-medium">Διπλοεγγραφές (ίδιος ΓΑΚ/Έτος)</Label>
            <Select value={duplicateAction} onValueChange={setDuplicateAction}>
              <SelectTrigger className="mt-1 border-amber-300" data-testid="duplicate-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Αγνόηση - Παράλειψη διπλοεγγραφών</SelectItem>
                <SelectItem value="update">Ενημέρωση - Ενημέρωση υπαρχουσών εγγραφών</SelectItem>
                <SelectItem value="replace">Αντικατάσταση - Αντικατάσταση όλων</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dropzone */}
          <div
            className={`dropzone ${dragActive ? "active" : ""}`}
            onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="dropzone"
          >
            <input ref={fileInputRef} type="file" className="hidden" accept=".xlsx,.xls,.ods,.csv"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} data-testid="file-input" />
            {file ? (
              <div className="flex items-center justify-center gap-4">
                <FileSpreadsheet className="w-12 h-12 text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }} data-testid="remove-file-btn">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400" />
                <p className="text-slate-600">Σύρετε το αρχείο εδώ ή κάντε κλικ για επιλογή</p>
                <p className="text-sm text-slate-400">Excel (.xlsx, .xls), LibreOffice (.ods), CSV</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          <Button onClick={handleUpload} disabled={!file || uploading} className="w-full" data-testid="upload-btn">
            {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Εισαγωγή...</>) : (<><Upload className="w-4 h-4 mr-2" />Εισαγωγή Υποθέσεων</>)}
          </Button>

          {/* Result */}
          {result && (
            <div className={`p-4 rounded-lg ${result.error ? "bg-red-50" : "bg-emerald-50"}`}>
              {result.error ? (
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-700">Σφάλμα Εισαγωγής</p>
                    <p className="text-sm text-red-600">{result.error}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-emerald-700">Εισαγωγή ολοκληρώθηκε</p>
                    <div className="mt-2 space-y-1 text-sm">
                      {result.imported_count > 0 && (
                        <p className="text-emerald-600">Νέες υποθέσεις: <strong>{result.imported_count}</strong></p>
                      )}
                      {result.updated_count > 0 && (
                        <p className="text-blue-600"><RefreshCw className="w-3.5 h-3.5 inline mr-1" />Ενημερώθηκαν: <strong>{result.updated_count}</strong></p>
                      )}
                      {typeof result.total_rows === "number" && (
                        <p className="text-slate-700" data-testid="import-total-rows">Γραμμές αρχείου: <strong>{result.total_rows}</strong> = νέες {result.imported_count} + ενημερώσεις {result.updated_count || 0} + διπλοεγγραφές {result.skipped_duplicates || 0} + σφάλματα {result.error_count || 0}</p>
                      )}
                      {result.column_mapping && (
                        <details className="text-xs text-slate-500 mt-1" data-testid="import-column-mapping">
                          <summary className="cursor-pointer">Αναγνώριση στηλών αρχείου ({Object.values(result.column_mapping).filter(Boolean).length}/{Object.keys(result.column_mapping).length})</summary>
                          <ul className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-4">
                            {Object.entries(result.column_mapping).map(([col, field]) => (
                              <li key={col}><span className="font-medium text-slate-700">{col}</span> → {field ? <span className="text-emerald-700">{field}</span> : <span className="text-red-600">δεν αναγνωρίστηκε</span>}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {result.missing_eak > 0 && (
                        <p className="text-slate-500" data-testid="import-missing-eak">Χωρίς ΕΑΚ (εισήχθησαν κανονικά): <strong>{result.missing_eak}</strong></p>
                      )}
                      {result.skipped_duplicates > 0 && (
                        <p className="text-amber-600">Διπλοεγγραφές (παραλείφθηκαν): <strong>{result.skipped_duplicates}</strong></p>
                      )}
                    </div>
                    {result.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-amber-600 font-medium">Σφάλματα ({result.errors.length}):</p>
                        <ul className="text-sm text-amber-600 mt-1 space-y-1">
                          {result.errors.map((err, i) => <li key={i}>• {err}</li>)}
                        </ul>
                      </div>
                    )}
                    <Button variant="link" className="p-0 h-auto mt-2 text-emerald-700" onClick={() => navigate("/cases")} data-testid="view-cases-btn">
                      Προβολή υποθέσεων →
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expected Columns */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Αναμενόμενες Στήλες</CardTitle>
          <CardDescription>Το αρχείο πρέπει να περιέχει τις παρακάτω στήλες</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {expectedColumns.map((col) => (
              <div key={col.name} className="flex items-center justify-between p-2 rounded bg-slate-50">
                <span className="text-sm text-slate-700">{col.name}</span>
                {col.required ? (
                  <Badge className="badge-urgent text-xs">Υποχρεωτικό</Badge>
                ) : (
                  <Badge className="bg-slate-200 text-slate-600 text-xs">Προαιρετικό</Badge>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-blue-900">Συμβουλές Εισαγωγής</h3>
            <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100"
              onClick={async () => {
                try {
                  const response = await axios.get(`${API}/cases/template`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'template_import.xlsx';
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch { toast.error("Σφάλμα λήψης προτύπου"); }
              }}
              data-testid="download-template-btn">
              <Upload className="w-4 h-4 mr-2 rotate-180" />Πρότυπο Excel
            </Button>
          </div>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Μοναδική ταυτότητα: ΓΑΚ Αριθμός + Έτος (δεν επιτρέπονται διπλοεγγραφές)</li>
            <li>• Επιλέξτε "Ενημέρωση" για να ενημερωθούν υπάρχουσες υποθέσεις με νέα στοιχεία</li>
            <li>• Η ημερομηνία πρέπει να είναι σε μορφή ΗΗ/ΜΜ/ΕΕΕΕ ή ΕΕΕΕ-ΜΜ-ΗΗ</li>
            <li>• Οι προθεσμίες υπολογίζονται αυτόματα βάσει της ημερομηνίας κατάθεσης</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportCases;
